// src/features/auth/components/AuthPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AuthLayout from './AuthLayout';
import SuccessModal from '../../../components/common/SuccessModal';
import { LoginResponse } from '../types';
import { authService } from '../services/authService';

const MACD_URL = import.meta.env.VITE_MACD_URL || 'http://localhost:8110';

const getOrigin = (value: string): string => {
  try {
    return new URL(value).origin;
  } catch {
    return '';
  }
};

const storeTokenExpiry = (accessToken: string): void => {
  try {
    const tokenPayload = accessToken.split('.')[1];
    if (!tokenPayload) throw new Error('Missing token payload');
    const normalizedPayload = tokenPayload.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalizedPayload));
    if (typeof payload.exp === 'number') {
      localStorage.setItem('token_expires_at', String(payload.exp * 1000));
      return;
    }
  } catch {
    // Fall through to clearing the expiry if the token shape is unexpected.
  }
  localStorage.removeItem('token_expires_at');
};

const AuthPage: React.FC = () => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle Google SSO handoff: Rapture redirects here with a one-time code.
  useEffect(() => {
    const handoffCode = searchParams.get('handoff_code');
    if (!handoffCode) return;

    const configuredOrigin = getOrigin(MACD_URL);
    const handoffOrigin = getOrigin(searchParams.get('handoff_origin') || MACD_URL);

    if (!configuredOrigin || configuredOrigin !== handoffOrigin) {
      authService.clearTokens();
      setModalTitle('Google sign-in failed');
      setModalMessage('The login handoff came from an unexpected source. Please try signing in again.');
      setIsSuccessModalOpen(true);
      navigate('/auth', { replace: true });
      return;
    }

    const controller = new AbortController();

    const exchangeHandoffCode = async () => {
      try {
        authService.clearTokens();
        const response = await fetch(configuredOrigin + '/api/auth/tesseract-handoff/exchange/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          signal: controller.signal,
          body: JSON.stringify({ handoff_code: handoffCode }),
        });

        if (!response.ok) {
          throw new Error('Handoff exchange failed with ' + response.status);
        }

        const data = await response.json();
        if (typeof data.access_token !== 'string' || typeof data.refresh_token !== 'string') {
          throw new Error('Handoff exchange did not return tokens');
        }

        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        storeTokenExpiry(data.access_token);
        authService.startTokenRefreshService();
        navigate('/autopilot', { replace: true });
      } catch (error) {
        if (controller.signal.aborted) return;
        authService.clearTokens();
        setModalTitle('Google sign-in failed');
        setModalMessage('Tesseract could not finish the Google sign-in handoff. Please try signing in again.');
        setIsSuccessModalOpen(true);
        navigate('/auth', { replace: true });
      }
    };

    void exchangeHandoffCode();

    return () => controller.abort();
  }, [navigate, searchParams]);

  // Watch for changes to isLoginView and clear tokens only when switching to registration
  // This prevents clearing valid tokens during 2FA setup while still solving the registration issue
  useEffect(() => {
    // Only clear tokens when user switches to registration view
    if (!isLoginView) {

      authService.clearTokens();
    }
  }, [isLoginView]);

  const handleLoginSuccess = async (data: LoginResponse) => {
    // Always navigate to autopilot after successful login
    // The onboarding modal will be handled by the MainAppLayout or another component
    navigate('/autopilot');
  };


  const toggleView = () => {
    setIsLoginView(!isLoginView);
  };

  const renderForm = () => {
    if (isLoginView) {
      return (
        <LoginForm 
          onSwitchToRegister={toggleView} 
          onLoginSuccess={handleLoginSuccess}
        />
      );
    }
    return (
      <RegisterForm 
        onSwitchToLogin={toggleView} 
        onRegisterSuccess={(data) => {
          
          setModalTitle('Registration Successful!');
          setModalMessage('Your account has been created.\nPlease log in to continue.');
          setIsSuccessModalOpen(true);
        }}
      />
    );
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setIsLoginView(true); // Switch to login view
  };

  return (
    <>
      <AuthLayout formSlot={renderForm()} />
      <SuccessModal 
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        title={modalTitle}
        message={modalMessage}
        buttonText="Proceed to Login"
      />
    </>
  );
};

export default AuthPage;
